import type { TokenModel } from "../../types/authentication";
import { httpClient } from "./httpClient";

export interface ResponsePayload<T = unknown> {
    payload?: T;
    code?: number;
    errorMessage?: string;
    isError?: boolean;
}

export interface AuthenticationResult {
    message: string;
    type: string;
}

export interface SignUpPasswordInput {
    firstName: string;
    lastName: string;
    phoneNumber?: string;
    countryCode?: string;
    email: string;
    password?: string;
    language?: string;
}

export interface SignUpTokenInput {
    email: string;
    password?: string;
    token: string;
    language?: string;
}

export interface FederatedSignInInput {
    provider: string;
    token: string;
    language?: string;
    email?: string;
}

export interface ResetPasswordInput {
    email: string;
    redirectUrl?: string;
}

export interface LoginHint {
    found: boolean;
    providerData?: { providerId: string }[];
}

export interface EmailExistsResponse {
    exists: boolean;
    loginHint?: LoginHint;
}

export const signInWithPassword = (email: string, password?: string): Promise<ResponsePayload<TokenModel>> => {
    return httpClient.post<ResponsePayload<TokenModel>>("/rest/auth/signin", { email, password }, { auth: false });
};

export const signInWithLink = (email: string, urlParam: string): Promise<ResponsePayload<TokenModel>> => {
    return httpClient.post<ResponsePayload<TokenModel>>(
        "/rest/auth/signin/link",
        { email, url: urlParam },
        { auth: false },
    );
};

export const signUpWithPassword = (input: SignUpPasswordInput): Promise<ResponsePayload<boolean>> => {
    return httpClient.post<ResponsePayload<boolean>>("/rest/auth/signup", input, { auth: false });
};

export const signUpWithInvitation = (input: SignUpTokenInput): Promise<ResponsePayload<TokenModel>> => {
    return httpClient.post<ResponsePayload<TokenModel>>("/rest/auth/signup/invitation", input, { auth: false });
};

export const signUpWithRTS = (input: SignUpTokenInput): Promise<ResponsePayload<TokenModel>> => {
    return httpClient.post<ResponsePayload<TokenModel>>("/rest/auth/signup/rts", input, { auth: false });
};

export const signInFederated = (input: FederatedSignInInput): Promise<ResponsePayload<TokenModel>> => {
    return httpClient.post<ResponsePayload<TokenModel>>("/rest/auth/federated", input, { auth: false });
};

export const refreshAuthToken = (refreshToken: string): Promise<ResponsePayload<TokenModel>> => {
    return httpClient.post<ResponsePayload<TokenModel>>("/rest/auth/token/refresh", { refreshToken }, { auth: false });
};

export const verifyEmailCode = (code: string, email: string): Promise<ResponsePayload<boolean>> => {
    return httpClient.post<ResponsePayload<boolean>>("/rest/auth/verify-email", { code, email }, { auth: false });
};

export const requestPasswordReset = (
    input: ResetPasswordInput,
): Promise<ResponsePayload<boolean> | AuthenticationResult> => {
    return httpClient.post<ResponsePayload<boolean> | AuthenticationResult>("/rest/auth/password/reset", input, {
        auth: false,
    });
};

export const confirmPasswordReset = (
    code: string,
    newPassword: string,
): Promise<ResponsePayload<boolean> | AuthenticationResult> => {
    return httpClient.post<ResponsePayload<boolean> | AuthenticationResult>(
        "/rest/auth/password/reset/confirm",
        { code, newPassword },
        { auth: false },
    );
};

export const setPasswordByLink = (
    email: string,
    password: string,
    urlParam: string,
): Promise<ResponsePayload<boolean>> => {
    return httpClient.post<ResponsePayload<boolean>>(
        "/rest/auth/password/link",
        { email, password, url: urlParam },
        { auth: false },
    );
};

export const checkEmailExists = (email: string): Promise<EmailExistsResponse> => {
    return httpClient.get<EmailExistsResponse>("/rest/auth/exists", {
        auth: false,
        params: { email },
    });
};

export const signOutUser = (): Promise<{ success: boolean }> => {
    return httpClient.post<{ success: boolean }>("/rest/auth/signout", undefined, { auth: true });
};
